import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../dashboard/components/AdminLayout';
import { COLORS } from '../../../../core/constants/colors';
import { adminBillingService } from '../services/admin-billing.service';
import { AdminSubscriptionPlan, CreatePlanRequest, UpdatePlanRequest } from '../types/billing.types';
import { PlanFormModal } from '../components/PlanFormModal';
import { DeletePlanModal } from '../components/DeletePlanModal';

export const AdminBillingPage: React.FC = () => {
  const [plans, setPlans] = useState<AdminSubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<AdminSubscriptionPlan | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setIsLoading(true);
    setError('');
    try {
      const plansData = await adminBillingService.getAllPlans();
      setPlans(plansData);
    } catch (err: any) {
      console.error('Error loading plans:', err);
      setError(err.message || 'Failed to load subscription plans');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePlan = async (planData: CreatePlanRequest | UpdatePlanRequest) => {
    setIsProcessing(true);
    setError('');
    try {
      // Type guard: if plan_id exists, it's a CreatePlanRequest
      if ('plan_id' in planData && planData.plan_id) {
        const newPlan = await adminBillingService.createPlan(planData as CreatePlanRequest);
        if (newPlan) {
          await loadPlans();
          setShowCreateModal(false);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create plan');
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdatePlan = async (planData: CreatePlanRequest | UpdatePlanRequest) => {
    if (!selectedPlan) return;

    setIsProcessing(true);
    setError('');
    try {
      const updatedPlan = await adminBillingService.updatePlan(selectedPlan.plan_id, planData as UpdatePlanRequest);
      if (updatedPlan) {
        await loadPlans();
        setShowEditModal(false);
        setSelectedPlan(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update plan');
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!selectedPlan) return;

    setIsProcessing(true);
    setError('');
    try {
      const success = await adminBillingService.deletePlan(selectedPlan.plan_id);
      if (success) {
        await loadPlans();
        setShowDeleteModal(false);
        setSelectedPlan(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete plan');
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const formatAmount = (amount: string, currency: string) => {
    try {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount)) return amount;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numAmount);
    } catch {
      return `${currency} ${amount}`;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Filter and search plans
  const filteredPlans = plans.filter((plan) => {
    const matchesSearch =
      plan.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plan.plan_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plan.plan_type.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter =
      filterActive === 'all' ||
      (filterActive === 'active' && plan.is_active) ||
      (filterActive === 'inactive' && !plan.is_active);

    return matchesSearch && matchesFilter;
  });

  // Sort plans by sort_order, then by name
  const sortedPlans = [...filteredPlans].sort((a, b) => {
    if (a.sort_order !== b.sort_order) {
      return a.sort_order - b.sort_order;
    }
    return a.name.localeCompare(b.name);
  });

  if (isLoading && plans.length === 0) {
    return (
      <AdminLayout pageTitle="Billing Plans Management" pageDescription="Manage subscription plans">
        <div style={{ backgroundColor: '#F5F5F5' }}>
          <div className="max-w-[1400px] mx-auto px-8 py-8">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-20 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Billing Plans Management" pageDescription="Create, edit, and manage subscription plans">
      <div style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-[1400px] mx-auto px-8 py-8">
          {error && (
            <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: '#FFEBEE' }}>
              <p className="text-sm" style={{ color: '#C62828' }}>{error}</p>
            </div>
          )}

          {/* Action Bar */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="relative">
                <svg
                  className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search plans..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: '#E5E7EB' }}
                />
              </div>
              <select
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'inactive')}
                className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
              >
                <option value="all">All Plans</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-all hover:shadow-md"
                style={{ backgroundColor: COLORS.admin.primary }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Create Plan
              </button>
            </div>
          </div>

          {/* Plans Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <thead style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Plan Name
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Plan ID
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Features
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedPlans.length > 0 ? (
                  sortedPlans.map((plan) => (
                    <tr
                      key={plan.id}
                      className="border-b hover:bg-gray-50 transition-colors"
                      style={{ borderColor: '#E5E7EB' }}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-sm" style={{ color: '#1F2937' }}>
                            {plan.name}
                            {plan.is_featured && (
                              <span
                                className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                                style={{ backgroundColor: COLORS.extended.orange }}
                              >
                                Featured
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">{plan.description}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-mono" style={{ color: '#6B7280' }}>
                          {plan.plan_id}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm capitalize" style={{ color: '#1F2937' }}>
                          {plan.plan_type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-semibold" style={{ color: COLORS.admin.primary }}>
                          {formatAmount(plan.amount, plan.currency)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm" style={{ color: '#1F2937' }}>
                          {plan.duration_days} {plan.duration_days === 1 ? 'day' : 'days'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {plan.features && plan.features.length > 0 ? (
                            plan.features.slice(0, 2).map((feature, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 rounded text-xs"
                                style={{ backgroundColor: COLORS.admin.primaryLight, color: COLORS.admin.primary }}
                              >
                                {feature}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">No features</span>
                          )}
                          {plan.features && plan.features.length > 2 && (
                            <span className="text-xs text-gray-500">+{plan.features.length - 2} more</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: plan.is_active ? '#D1FAE5' : '#FEE2E2',
                            color: plan.is_active ? '#065F46' : '#991B1B',
                          }}
                        >
                          {plan.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs text-gray-500">{formatDate(plan.created_at)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPlan(plan);
                              setShowEditModal(true);
                            }}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-all"
                            style={{ color: COLORS.admin.primary }}
                            title="Edit plan"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPlan(plan);
                              setShowDeleteModal(true);
                            }}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-all"
                            style={{ color: COLORS.extended.redSalsa }}
                            title="Delete plan"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                          style={{ backgroundColor: COLORS.admin.primaryLight }}
                        >
                          <svg
                            className="w-8 h-8"
                            style={{ color: COLORS.admin.primary }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <p className="text-gray-600 font-medium mb-2">No plans found</p>
                        <p className="text-sm text-gray-500">Create your first subscription plan to get started</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Plan Modal */}
      <PlanFormModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setError('');
        }}
        onSubmit={handleCreatePlan}
        isLoading={isProcessing}
      />

      {/* Edit Plan Modal */}
      <PlanFormModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedPlan(null);
          setError('');
        }}
        onSubmit={handleUpdatePlan}
        plan={selectedPlan}
        isLoading={isProcessing}
      />

      {/* Delete Plan Modal */}
      <DeletePlanModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedPlan(null);
          setError('');
        }}
        onConfirm={handleDeletePlan}
        plan={selectedPlan}
        isLoading={isProcessing}
      />
    </AdminLayout>
  );
};
